import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/table';
import { Textarea } from '../../components/ui/textarea';
import { Calendar, PenLine, BarChart3, Megaphone, Clock, Upload, School, FileText, BookOpen, Users } from 'lucide-react';
import ReportCard from '../../components/ReportCard';
import { toast } from 'sonner';

export default function StudentDashboard() {
  const location = useLocation();
  const pathTab = location.pathname.split('/').pop();
  const tabMap = { timetable: 'timetable', assignments: 'assignments', homework: 'homework', results: 'results', 'report-card': 'report-card', 'class-info': 'class-info', announcements: 'announcements' };
  const [activeTab, setActiveTab] = useState(tabMap[pathTab] || 'dashboard');

  React.useEffect(() => {
    setActiveTab(tabMap[pathTab] || 'dashboard');
  }, [pathTab]);

  const [dashboard, setDashboard] = useState(null);
  const [fullTimetable, setFullTimetable] = useState([]);
  const [allAssignments, setAllAssignments] = useState([]);
  const [allResults, setAllResults] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [classInfo, setClassInfo] = useState(null);
  const [uploadAssignment, setUploadAssignment] = useState(null);
  const [homeworkFile, setHomeworkFile] = useState(null);
  const [homeworkComment, setHomeworkComment] = useState('');

  useEffect(() => {
    api.get('/student/dashboard').then(r => setDashboard(r.data)).catch(() => {});
    api.get('/student/timetable').then(r => setFullTimetable(r.data)).catch(() => {});
    api.get('/student/assignments').then(r => setAllAssignments(r.data)).catch(() => {});
    api.get('/student/results').then(r => setAllResults(r.data)).catch(() => {});
    api.get('/student/announcements').then(r => setAnnouncements(r.data)).catch(() => {});
    api.get('/student/submissions').then(r => setSubmissions(r.data)).catch(() => {});
    api.get('/student/class-info').then(r => setClassInfo(r.data)).catch(() => {});
  }, []);

  const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
  const groupedTT = DAYS.reduce((acc, d) => { acc[d] = fullTimetable.filter(e => e.day === d).sort((a, b) => a.period - b.period); return acc; }, {});

  const submitHomework = async (assignmentId) => {
    if (!homeworkFile) { toast.error('Please select a file'); return; }
    const fd = new FormData();
    fd.append('file', homeworkFile);
    fd.append('comment', homeworkComment);
    try {
      await api.post(`/student/assignments/${assignmentId}/submit`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Homework submitted!');
      setUploadAssignment(null); setHomeworkFile(null); setHomeworkComment('');
      api.get('/student/submissions').then(r => setSubmissions(r.data));
    } catch (e) { toast.error('Submission failed'); }
  };

  const getSubmission = (aId) => submissions.find(s => s.assignment_id === aId);

  return (
    <div data-testid="student-dashboard">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#1B2A4A]">Student Portal</h1>
        {dashboard?.student && <p className="text-sm text-slate-500 mt-1">{dashboard.student.first_name} {dashboard.student.last_name} | {dashboard.student.grade} | {dashboard.student.admission_number}</p>}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6 flex-wrap h-auto gap-1">
          <TabsTrigger value="dashboard"><Clock className="w-3.5 h-3.5 mr-1" /> Overview</TabsTrigger>
          <TabsTrigger value="timetable"><Calendar className="w-3.5 h-3.5 mr-1" /> Timetable</TabsTrigger>
          <TabsTrigger value="assignments"><PenLine className="w-3.5 h-3.5 mr-1" /> Assignments</TabsTrigger>
          <TabsTrigger value="homework"><Upload className="w-3.5 h-3.5 mr-1" /> Homework</TabsTrigger>
          <TabsTrigger value="results"><BarChart3 className="w-3.5 h-3.5 mr-1" /> Results</TabsTrigger>
          <TabsTrigger value="report-card"><FileText className="w-3.5 h-3.5 mr-1" /> Report Card</TabsTrigger>
          <TabsTrigger value="class-info"><School className="w-3.5 h-3.5 mr-1" /> My Class</TabsTrigger>
          <TabsTrigger value="announcements"><Megaphone className="w-3.5 h-3.5 mr-1" /> News</TabsTrigger>
        </TabsList>

        {/* Overview */}
        <TabsContent value="dashboard">
          {dashboard && (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="stat-card"><p className="text-xs text-slate-500">Today's Classes</p><p className="text-2xl font-black text-[#1B2A4A]">{dashboard.today_timetable?.length || 0}</p></div>
                <div className="stat-card"><p className="text-xs text-slate-500">Assignments Due</p><p className="text-2xl font-black text-amber-600">{dashboard.upcoming_assignments?.length || 0}</p></div>
                <div className="stat-card"><p className="text-xs text-slate-500">Recent Grades</p><p className="text-2xl font-black text-emerald-600">{dashboard.recent_grades?.length || 0}</p></div>
                <div className="stat-card"><p className="text-xs text-slate-500">Attendance</p><p className="text-2xl font-black text-sky-600">{dashboard.attendance?.percentage || 100}%</p></div>
              </div>
              {dashboard.today_timetable?.length > 0 && (
                <Card className="mb-6"><CardHeader><CardTitle className="text-base">Today's Schedule</CardTitle></CardHeader><CardContent>
                  {dashboard.today_timetable.map((e, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg mb-2 last:mb-0">
                      <div><p className="font-semibold text-sm">Period {e.period}: {e.subject_name}</p><p className="text-xs text-slate-500">{e.teacher_name} | {e.start_time} - {e.end_time}</p></div>
                      <Badge variant="outline">{e.room || 'TBA'}</Badge>
                    </div>
                  ))}
                </CardContent></Card>
              )}
            </>
          )}
        </TabsContent>

        {/* Timetable */}
        <TabsContent value="timetable">
          {DAYS.map(day => groupedTT[day]?.length > 0 && (
            <div key={day} className="mb-4">
              <h3 className="font-bold text-[#1B2A4A] mb-2 text-sm">{day}</h3>
              <div className="space-y-2">{groupedTT[day].map(e => (
                <div key={e.id} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                  <div><p className="font-semibold text-sm">Period {e.period}: {e.subject_name}</p><p className="text-xs text-slate-500">{e.teacher_name} | {e.start_time} - {e.end_time}</p></div>
                  <Badge variant="outline">{e.room || 'TBA'}</Badge>
                </div>
              ))}</div>
            </div>
          ))}
          {fullTimetable.length === 0 && <p className="text-slate-400 text-center py-8">No timetable assigned yet</p>}
        </TabsContent>

        {/* Assignments */}
        <TabsContent value="assignments">
          {allAssignments.length === 0 ? <p className="text-slate-400 text-center py-8">No assignments yet</p> : (
            <div className="space-y-3">{allAssignments.map(a => {
              const sub = getSubmission(a.id);
              return (
                <Card key={a.id}><CardContent className="py-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <p className="font-bold text-[#1B2A4A]">{a.title}</p>
                      <p className="text-xs text-slate-500">{a.subject_name} | Due: {a.due_date}</p>
                      {a.instructions && <p className="text-sm text-slate-600 mt-2 line-clamp-2">{a.instructions}</p>}
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <Badge variant={sub ? 'default' : 'outline'} className={sub ? 'bg-emerald-100 text-emerald-700' : ''}>
                        {sub ? 'Submitted' : new Date(a.due_date) > new Date() ? 'Pending' : 'Past Due'}
                      </Badge>
                      {!sub && (
                        <Button size="sm" variant="outline" className="text-xs mt-1" onClick={() => { setUploadAssignment(a); setActiveTab('homework'); }}>
                          <Upload className="w-3 h-3 mr-1" /> Submit
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent></Card>
              );
            })}</div>
          )}
        </TabsContent>

        {/* Homework Upload */}
        <TabsContent value="homework">
          <Card><CardHeader><CardTitle className="text-base">Submit Homework</CardTitle></CardHeader><CardContent>
            {allAssignments.length === 0 ? <p className="text-slate-400 text-sm">No assignments to submit homework for</p> : (
              <div className="space-y-4">
                {allAssignments.map(a => {
                  const sub = getSubmission(a.id);
                  const isSelected = uploadAssignment?.id === a.id;
                  return (
                    <div key={a.id} className={`p-4 rounded-xl border ${isSelected ? 'border-[#E8A830] bg-amber-50' : 'border-slate-100'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="font-bold text-sm text-[#1B2A4A]">{a.title}</p>
                          <p className="text-xs text-slate-500">{a.subject_name} | Due: {a.due_date}</p>
                        </div>
                        {sub ? <Badge className="bg-emerald-100 text-emerald-700 text-xs">Submitted</Badge> :
                          <Button size="sm" variant={isSelected ? "default" : "outline"} className={isSelected ? 'bg-[#1B2A4A]' : ''} onClick={() => setUploadAssignment(isSelected ? null : a)}>
                            {isSelected ? 'Selected' : 'Select'}
                          </Button>}
                      </div>
                      {isSelected && !sub && (
                        <div className="mt-3 pt-3 border-t border-amber-200 space-y-3">
                          <Input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={e => setHomeworkFile(e.target.files[0])} data-testid="homework-file-input" />
                          {homeworkFile && (
                            <div className="flex items-center gap-2 bg-slate-50 rounded-lg p-2.5">
                              <FileText className="w-4 h-4 text-[#1B2A4A] flex-shrink-0" />
                              <div className="min-w-0 flex-1">
                                <p className="text-xs font-semibold text-[#1B2A4A] truncate">{homeworkFile.name}</p>
                                <p className="text-[10px] text-slate-400">{(homeworkFile.size / 1024).toFixed(1)} KB</p>
                              </div>
                            </div>
                          )}
                          <Textarea placeholder="Add a comment (optional)..." value={homeworkComment} onChange={e => setHomeworkComment(e.target.value)} rows={2} />
                          <Button onClick={() => submitHomework(a.id)} className="bg-[#E8A830] hover:bg-[#D4982A] text-white rounded-full" data-testid="submit-homework-btn">
                            <Upload className="w-4 h-4 mr-1" /> Submit Homework
                          </Button>
                        </div>
                      )}
                      {sub && (
                        <div className="mt-2 flex items-center gap-2">
                          <Badge className="bg-emerald-100 text-emerald-700 text-xs">Submitted</Badge>
                          {sub.files?.length > 0 && <span className="text-[10px] text-slate-400">{sub.files.length} file(s)</span>}
                          <span className="text-xs text-slate-400">{new Date(sub.submitted_at).toLocaleString()}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent></Card>
        </TabsContent>

        {/* Results */}
        <TabsContent value="results">
          {allResults.length === 0 ? <p className="text-slate-400 text-center py-8">No results yet</p> : (
            <div className="bg-white rounded-xl border shadow-sm">
              <Table>
                <TableHeader><TableRow className="data-table"><TableHead>Subject</TableHead><TableHead>Assessment</TableHead><TableHead>Marks</TableHead><TableHead>Grade</TableHead><TableHead>Comments</TableHead></TableRow></TableHeader>
                <TableBody>{allResults.map(g => (
                  <TableRow key={g.id}>
                    <TableCell className="font-semibold">{g.subject_name}</TableCell>
                    <TableCell>{g.assessment_name}</TableCell>
                    <TableCell className="font-bold">{g.marks}/{g.total_marks}</TableCell>
                    <TableCell><Badge className={g.grade_letter === 'F' ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}>{g.grade_letter}</Badge></TableCell>
                    <TableCell className="text-sm text-slate-500">{g.comments}</TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table>
            </div>
          )}
        </TabsContent>

        {/* Report Card */}
        <TabsContent value="report-card">
          {dashboard?.student?.id ? <ReportCard studentId={dashboard.student.id} /> : <p className="text-slate-400 text-center py-8">No student profile found</p>}
        </TabsContent>

        {/* My Class */}
        <TabsContent value="class-info">
          {classInfo ? (
            <div className="space-y-6">
              {classInfo.class?.photo_url && (
                <div className="rounded-xl overflow-hidden border shadow-sm">
                  <img
                    src={classInfo.class.photo_url.startsWith('http') ? classInfo.class.photo_url : `${process.env.REACT_APP_BACKEND_URL}/api/public/files/${classInfo.class.photo_url}`}
                    alt={`${classInfo.class.name} class photo`}
                    className="w-full h-48 sm:h-64 object-cover"
                    data-testid="class-photo"
                  />
                </div>
              )}
              {classInfo.class && (
                <Card><CardHeader><CardTitle className="text-base">Class Details</CardTitle></CardHeader><CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div><p className="text-xs text-slate-500">Class</p><p className="font-bold">{classInfo.class.name}</p></div>
                    <div><p className="text-xs text-slate-500">Division</p><p className="font-bold">{classInfo.class.division}</p></div>
                    <div><p className="text-xs text-slate-500">Grade</p><p className="font-bold">{classInfo.class.grade}</p></div>
                    <div><p className="text-xs text-slate-500">Room</p><p className="font-bold">{classInfo.class.room || 'TBA'}</p></div>
                  </div>
                </CardContent></Card>
              )}
              {classInfo.teachers?.length > 0 && (
                <Card><CardHeader><CardTitle className="text-base flex items-center gap-2"><Users className="w-4 h-4" /> Teachers</CardTitle></CardHeader><CardContent>
                  <div className="space-y-2">{classInfo.teachers.map(t => (
                    <div key={t.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div>
                        <p className="font-semibold text-sm">{t.first_name} {t.last_name} {t.is_class_teacher && <Badge className="ml-2 bg-amber-100 text-amber-700 text-[10px]">Class Teacher</Badge>}</p>
                        <p className="text-xs text-slate-500">{t.subjects?.join(', ') || 'General'}</p>
                      </div>
                    </div>
                  ))}</div>
                </CardContent></Card>
              )}
              {classInfo.classmates?.length > 0 && (
                <Card><CardHeader><CardTitle className="text-base flex items-center gap-2"><BookOpen className="w-4 h-4" /> Classmates ({classInfo.classmates.length})</CardTitle></CardHeader><CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">{classInfo.classmates.map(c => (
                    <div key={c.id} className="bg-slate-50 rounded-lg p-2.5 text-sm font-medium">{c.first_name} {c.last_name}</div>
                  ))}</div>
                </CardContent></Card>
              )}
            </div>
          ) : <p className="text-slate-400 text-center py-8">No class assigned yet</p>}
        </TabsContent>

        {/* Announcements */}
        <TabsContent value="announcements">
          {announcements.length === 0 ? <p className="text-slate-400 text-center py-8">No announcements</p> : (
            <div className="space-y-3">{announcements.map(a => (
              <Card key={a.id}><CardContent className="py-4">
                <p className="font-bold text-[#1B2A4A]">{a.title}</p>
                <p className="text-xs text-slate-400 mt-0.5">{new Date(a.created_at).toLocaleDateString()} | {a.author_name || 'Admin'}</p>
                <p className="text-sm text-slate-600 mt-2 whitespace-pre-wrap">{a.content}</p>
              </CardContent></Card>
            ))}</div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
